import React, { createContext, useContext, useMemo, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from "../design/dialog";
import { Button } from "../design/button";
import { useFinancialAccount } from "../contexts/FinancialAccountContext";
import { Table, TableBody, TableCell, TableRow } from "../design/table";
import FinancialAccountFilters from "../components/Dialogs/ChooseFinancialAccountDialog/components/FinancialAccountFilters";
import PlaidButton from "../components/PlaidButton";
import { FinancialAccount } from "../models/FinancialAccount";
import { AccountType } from "plaid";
import EmptyStateView from "../components/EmptyStateView";
import { Subheading } from "../design/heading";

interface Context {
  openDialog: (
    type?: AccountType,
    onSelect?: (account: FinancialAccount) => void,
  ) => void;
  closeDialog: () => void;
  isOpen: boolean;
}

const ChooseFinancialAccountDialogProviderContext = createContext<
  Context | undefined
>(undefined);

export const ChooseFinancialAccountDialogProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { financialAccounts } = useFinancialAccount();
  const [type, setType] = useState<AccountType | undefined>(undefined);
  const [isOpen, setIsOpen] = useState(false);
  const [onSelectCallback, setOnSelectCallback] = useState<
    ((account: FinancialAccount) => void) | undefined
  >(undefined);

  const openDialog = (
    type?: AccountType,
    onSelect?: (account: FinancialAccount) => void,
  ) => {
    setType(type);
    setOnSelectCallback(() => onSelect);
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
    setType(undefined);
    setOnSelectCallback(undefined);
  };

  const handleSelect = (account: FinancialAccount) => {
    if (onSelectCallback) {
      onSelectCallback(account);
    }
    closeDialog();
  };

  const filteredAccounts = useMemo(() => {
    return type
      ? financialAccounts.filter((f) => f.type === type)
      : financialAccounts;
  }, [financialAccounts, type]);

  return (
    <ChooseFinancialAccountDialogProviderContext.Provider
      value={{ openDialog, closeDialog, isOpen }}
    >
      {isOpen && (
        <Dialog open={isOpen} onClose={closeDialog}>
          <DialogTitle className="flex items-center justify-between">
            <span>Choose Financial Account</span>
            <PlaidButton>Add Plaid Account</PlaidButton>
          </DialogTitle>

          <DialogDescription>
            Select a financial account to proceed.
            <div className="mt-2">
              <FinancialAccountFilters type={type} setType={setType} />
            </div>
          </DialogDescription>

          <DialogBody>
            {filteredAccounts.length > 0 ? (
              <Table>
                <TableBody>
                  {filteredAccounts.map((f) => (
                    <TableRow
                      key={f._id}
                      onClick={() => handleSelect(f)}
                      className="cursor-pointer"
                    >
                      <TableCell>
                        <div className="flex flex-col">
                          <Subheading>{`${f.name} (${f.mask})`}</Subheading>
                          <span className="text-sm text-gray-500">
                            {f.subType}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: f.currency,
                        }).format(f.currentBalance)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <EmptyStateView title="No Financial Accounts Found" />
            )}
          </DialogBody>

          <DialogActions>
            <Button plain onClick={closeDialog}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {children}
    </ChooseFinancialAccountDialogProviderContext.Provider>
  );
};

export const useChooseFinancialAccountDialog = () => {
  const ctx = useContext(ChooseFinancialAccountDialogProviderContext);
  if (!ctx)
    throw new Error(
      "useChooseFinancialAccountDialog must be used inside ChooseFinancialAccountDialogProvider",
    );
  return ctx;
};
